import * as Dialog from "@radix-ui/react-dialog"
import * as Portal from "@radix-ui/react-portal"
import { useMutation } from "@blitzjs/rpc"
import { useRouter } from "next/router"
import { useState } from "react"
import deleteAccount from "app/auth/mutations/deleteAccount"

export function ManageAccountModal({ close }: { close: () => void }) {
  const router = useRouter()
  const [deleteAccountMutation, { isLoading }] = useMutation(deleteAccount)
  const [step, setStep] = useState<"manage" | "confirm">("manage")
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleDelete() {
    try {
      await deleteAccountMutation({})
      await router.push("/")
      close()
    } catch {
      setError("Something went wrong. Please try again.")
    }
  }

  function handleBack() {
    setStep("manage")
    setConfirmed(false)
    setError(null)
  }

  if (step === "confirm") {
    return (
      <Portal.Root>
        <Dialog.Overlay className="fixed inset-0 z-10 bg-black bg-opacity-50 transition-all" />
        <Dialog.Content className="fixed left-1/2 top-20 z-10 -mx-48 w-96 rounded-md bg-white p-6 text-left shadow-lg">
          <Dialog.Title className="pb-2 text-lg font-semibold text-brand-green-dark">
            Delete your account
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            Permanently delete your account and all of your scenarios.
          </Dialog.Description>
          <label className="mb-4 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={isLoading}
              className="mt-0.5 h-4 w-4 shrink-0 accent-red-600"
            />
            <span className="text-sm text-gray-700">
              I understand this will permanently delete my account and all of my scenarios.
            </span>
          </label>
          {error && <p className="pb-4 text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </button>
            <button
              className="rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              onClick={handleDelete}
              disabled={!confirmed || isLoading}
            >
              {isLoading ? "Deleting…" : "Delete my account"}
            </button>
          </div>
        </Dialog.Content>
      </Portal.Root>
    )
  }

  return (
    <Portal.Root>
      <Dialog.Overlay className="fixed inset-0 z-10 bg-black bg-opacity-50 transition-all" />
      <Dialog.Content className="fixed left-1/2 top-20 z-10 -mx-48 w-96 rounded-md bg-white p-6 text-left shadow-lg">
        <Dialog.Title className="pb-2 text-lg font-semibold text-brand-green-dark">
          Manage Account
        </Dialog.Title>
        <Dialog.Description className="pb-6 text-sm text-gray-600">
          Manage your account settings and data.
        </Dialog.Description>
        <div className="flex justify-between">
          <button
            className="rounded border border-red-600 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
            onClick={() => setStep("confirm")}
          >
            Delete Account
          </button>
          <button
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
            onClick={close}
          >
            Close
          </button>
        </div>
      </Dialog.Content>
    </Portal.Root>
  )
}
